/**
 * shop-by-age-cta-carousel — InfiniteCarousel subclass for the Shop By Age
 * Page CTA section.
 *
 * Adds one behavior on top of the generic base: as the active slide changes,
 * re-tint the section surface + waves to that slide's color. Each slide <li>
 * carries `data-slide-bg` (the schema `background_color` value); the section
 * root (`.shop-by-age-page-cta`) carries `data-bg`, which the section CSS maps
 * to a brand-token background on `__bg-rect` and a matching `currentColor`
 * fill on the wave SVGs. Swapping `data-bg` + a CSS `transition` on those two
 * layers produces the cross-fade as the content slides.
 *
 * The base dispatches `slideChanged` during the rAF-throttled scroll (not just
 * at settle), so the color starts moving while the slide is still gliding.
 * Clones built by the loop carry `data-slide-bg` too (they're cloneNode(true)),
 * so looped slides re-tint correctly.
 *
 * Reduced motion is handled entirely in CSS (the transition is dropped under
 * prefers-reduced-motion) — the data-bg swap itself stays instant either way.
 *
 * It also owns the per-slide CTA's "scroll to top" action: a slide whose
 * `button_link` resolves to the current page renders a <button data-scroll-top>
 * (server-side, in the section Liquid) instead of a navigation <a>. A single
 * delegated click listener smooth-scrolls the window back to the top of the
 * page — instant under prefers-reduced-motion.
 *
 * Depends on infinite-carousel.js being loaded first (defer preserves order).
 */
class ShopByAgeCtaCarousel extends (window.InfiniteCarousel || HTMLElement) {
  connectedCallback() {
    super.connectedCallback();

    this._surface = this.closest('.shop-by-age-page-cta');
    if (!this._surface) return;

    this._onSlideChanged = (event) => {
      const slide = event.detail && event.detail.currentElement;
      const bg = slide && slide.dataset ? slide.dataset.slideBg : null;
      if (bg) this._surface.setAttribute('data-bg', bg);
    };

    this.addEventListener('slideChanged', this._onSlideChanged);

    this._onScrollTopClick = (event) => {
      const trigger = event.target.closest('[data-scroll-top]');
      if (!trigger || !this.contains(trigger)) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    this.addEventListener('click', this._onScrollTopClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._onSlideChanged) {
      this.removeEventListener('slideChanged', this._onSlideChanged);
    }
    if (this._onScrollTopClick) {
      this.removeEventListener('click', this._onScrollTopClick);
    }
  }
}

if (window.InfiniteCarousel && !customElements.get('shop-by-age-cta-carousel')) {
  customElements.define('shop-by-age-cta-carousel', ShopByAgeCtaCarousel);
}
